import { computed, signal } from '@preact/signals'
import { render } from 'preact'
import { useEffect } from 'preact/hooks'
import { bffFetch } from './api'
import {
  dateInputValue,
  dayRange,
  sumBy,
  type DailyNote,
  type FeedingRecord,
  type HydrationRecord,
  type Parrot,
  type WeightRecord,
} from './dashboard'
import './styles.css'

const authenticated = signal<boolean | null>(null)
const parrots = signal<Parrot[]>([])
const selectedParrotId = signal<string | null>(null)
const selectedDate = signal(dateInputValue())
const weights = signal<WeightRecord[]>([])
const feedings = signal<FeedingRecord[]>([])
const hydrations = signal<HydrationRecord[]>([])
const dailyNote = signal<DailyNote | null>(null)
const errorMessage = signal<string | null>(null)
const recordsLoading = signal(false)
const registrationOpen = signal(false)
const registrationSaving = signal(false)

const selectedParrot = computed(
  () => parrots.value.find((parrot) => parrot.id === selectedParrotId.value) ?? null,
)
const latestWeight = computed(() => weights.value[0]?.weight_grams ?? null)
const feedingTotal = computed(() => sumBy(feedings.value, (record) => record.food_amount_grams))
const hydrationTotal = computed(() => sumBy(hydrations.value, (record) => record.amount_ml))

async function readJson<T>(response: Response, message: string): Promise<T> {
  if (!response.ok) throw new Error(message)
  return response.json() as Promise<T>
}

async function loadDashboard(): Promise<void> {
  if (!selectedParrotId.value) return

  recordsLoading.value = true
  errorMessage.value = null
  const parrotId = encodeURIComponent(selectedParrotId.value)
  const date = selectedDate.value
  const range = dayRange(date)
  const commonQuery = `parrot_id=${parrotId}&start_date=${encodeURIComponent(range.start)}&end_date=${encodeURIComponent(range.end)}`

  try {
    const [weightResponse, feedingResponse, hydrationResponse, noteResponse] = await Promise.all([
      bffFetch(`/bff/api/weight?${commonQuery}`),
      bffFetch(`/bff/api/feeding?${commonQuery}`),
      bffFetch(`/bff/api/hydration?${commonQuery}`),
      bffFetch(`/bff/api/daily-notes/find-by-date?parrot_id=${parrotId}&date=${date}`),
    ])

    const [weightRecords, feedingRecords, hydrationRecords, note] = await Promise.all([
      readJson<WeightRecord[]>(weightResponse, '体重記録を取得できませんでした'),
      readJson<FeedingRecord[]>(feedingResponse, '食事記録を取得できませんでした'),
      readJson<HydrationRecord[]>(hydrationResponse, '水分記録を取得できませんでした'),
      readJson<DailyNote | null>(noteResponse, '日記を取得できませんでした'),
    ])

    weights.value = weightRecords
    feedings.value = feedingRecords
    hydrations.value = hydrationRecords
    dailyNote.value = note
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '記録を取得できませんでした'
  } finally {
    recordsLoading.value = false
  }
}

async function loadSession(): Promise<void> {
  try {
    const sessionResponse = await bffFetch('/bff/auth/session')
    authenticated.value = sessionResponse.ok
    if (!sessionResponse.ok) return

    parrots.value = await readJson<Parrot[]>(
      await bffFetch('/bff/api/parrots'),
      'インコ一覧を取得できませんでした',
    )
    selectedParrotId.value = parrots.value[0]?.id ?? null
    await loadDashboard()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '予期せぬエラーが発生しました'
  }
}

async function logout(): Promise<void> {
  await bffFetch('/bff/auth/logout', { method: 'POST' })
  authenticated.value = false
  parrots.value = []
}

async function registerParrot(event: Event): Promise<void> {
  event.preventDefault()
  if (registrationSaving.value) return

  registrationSaving.value = true
  errorMessage.value = null
  const form = event.currentTarget as HTMLFormElement
  const data = new FormData(form)
  const initialWeight = String(data.get('initial_weight') ?? '').trim()
  const body = {
    birth_date: String(data.get('birth_date') ?? '') || undefined,
    gender: String(data.get('gender') ?? '') || undefined,
    initial_weight: initialWeight ? Number(initialWeight) : undefined,
    name: String(data.get('name') ?? '').trim(),
    species: String(data.get('species') ?? '').trim() || undefined,
  }

  try {
    const created = await readJson<Parrot>(
      await bffFetch('/bff/api/parrots', {
        body: JSON.stringify(body),
        method: 'POST',
      }),
      'インコを登録できませんでした',
    )
    parrots.value = [created, ...parrots.value]
    selectedParrotId.value = created.id
    registrationOpen.value = false
    form.reset()
    await loadDashboard()
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'インコを登録できませんでした'
  } finally {
    registrationSaving.value = false
  }
}

function RecordCard(props: { children: preact.ComponentChildren; label: string; value: string }) {
  return (
    <article class="record-card">
      <div class="record-heading">
        <p>{props.label}</p>
        <strong>{props.value}</strong>
      </div>
      <div class="record-detail">{props.children}</div>
    </article>
  )
}

function RegistrationForm() {
  return (
    <section class="registration-card" aria-label="インコを登録">
      <div class="form-heading">
        <div>
          <p class="eyebrow">はじめに</p>
          <h2>インコちゃんを登録</h2>
        </div>
        {parrots.value.length > 0 && (
          <button class="text-button" onClick={() => { registrationOpen.value = false }} type="button">閉じる</button>
        )}
      </div>
      <form class="registration-form" onSubmit={(event) => void registerParrot(event)}>
        <label>名前<input name="name" required /></label>
        <label>種類<input name="species" placeholder="セキセイインコ" /></label>
        <label>性別
          <select defaultValue="不明" name="gender">
            <option>不明</option>
            <option>オス</option>
            <option>メス</option>
          </select>
        </label>
        <label>誕生日<input max={dateInputValue()} name="birth_date" type="date" /></label>
        <label>初期体重（g）<input inputMode="decimal" min="0.1" name="initial_weight" step="0.1" type="number" /></label>
        <button class="primary-action" disabled={registrationSaving.value} type="submit">
          {registrationSaving.value ? '登録中...' : '登録する'}
        </button>
      </form>
    </section>
  )
}

function Dashboard() {
  return (
    <main class="app-shell">
      <header>
        <div>
          <p class="eyebrow">インコ健康管理</p>
          <h1>今日の記録</h1>
        </div>
        <button class="text-button" type="button" onClick={() => void logout()}>ログアウト</button>
      </header>

      <section class="parrot-selector" aria-label="インコを選択">
        {parrots.value.map((parrot) => (
          <button
            aria-pressed={selectedParrotId.value === parrot.id}
            class="parrot-choice"
            key={parrot.id}
            onClick={() => {
              selectedParrotId.value = parrot.id
              void loadDashboard()
            }}
            type="button"
          >
            <span class="avatar">{parrot.name.slice(0, 1)}</span>
            <span>{parrot.name}</span>
          </button>
        ))}
        <button class="parrot-choice add-parrot" onClick={() => { registrationOpen.value = true }} type="button">
          <span class="avatar">+</span>
          <span>追加</span>
        </button>
      </section>

      {registrationOpen.value && <RegistrationForm />}

      <div class="date-row">
        <div>
          <p class="eyebrow">選択中</p>
          <h2>{selectedParrot.value?.name}</h2>
        </div>
        <input
          aria-label="記録の日付"
          max={dateInputValue()}
          onChange={(event) => {
            selectedDate.value = event.currentTarget.value
            void loadDashboard()
          }}
          type="date"
          value={selectedDate.value}
        />
      </div>

      {errorMessage.value && <p class="error">{errorMessage.value}</p>}
      {recordsLoading.value && <p class="loading-line">記録を読み込み中...</p>}

      <section class="record-grid" aria-label="選択日の健康記録">
        <RecordCard label="体重" value={latestWeight.value === null ? '記録なし' : `${latestWeight.value} g`}>
          {weights.value.length > 1 ? `${weights.value.length}件の記録` : '最新の測定値'}
        </RecordCard>
        <RecordCard label="食事" value={feedings.value.length === 0 ? '記録なし' : `${feedingTotal.value} g`}>
          {feedings.value.length === 0 ? '食事量を記録しましょう' : `${feedings.value.length}件の合計`}
        </RecordCard>
        <RecordCard label="水分" value={hydrations.value.length === 0 ? '記録なし' : `${hydrationTotal.value} ml`}>
          {hydrations.value.length === 0 ? '飲水量を記録しましょう' : `${hydrations.value.length}件の合計`}
        </RecordCard>
        <RecordCard label="日記" value={dailyNote.value ? '記録あり' : '記録なし'}>
          {dailyNote.value?.content ?? 'その日の様子を残しましょう'}
        </RecordCard>
      </section>

      <nav class="bottom-nav" aria-label="メインメニュー">
        <button aria-current="page" type="button">ホーム</button>
        <button disabled type="button">アルバム</button>
        <button disabled type="button">統計</button>
      </nav>
    </main>
  )
}

function App() {
  useEffect(() => {
    void loadSession()
  }, [])

  if (authenticated.value === null) {
    return <main class="center"><p>読み込み中...</p></main>
  }

  if (!authenticated.value) {
    return (
      <main class="login">
        <div class="bird" aria-hidden="true">IN</div>
        <h1>大切な家族の<br />毎日の記録をはじめましょう</h1>
        <p>Googleアカウントで手軽に始められます</p>
        {errorMessage.value && <p class="error">{errorMessage.value}</p>}
        <a class="primary-button" href="/bff/auth/login">Googleでログイン</a>
      </main>
    )
  }

  if (parrots.value.length === 0) {
    return (
      <main class="app-shell">
        <header><h1>今日の記録</h1><button class="text-button" type="button" onClick={() => void logout()}>ログアウト</button></header>
        {errorMessage.value && <p class="error">{errorMessage.value}</p>}
        <RegistrationForm />
      </main>
    )
  }

  return <Dashboard />
}

render(<App />, document.getElementById('app')!)
